<template>
  <div class="about-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero -->
    <section class="about-hero">
      <div class="about-hero-inner">
        <div class="about-hero-text" data-aos="fade-up">
          <span class="section-eyebrow">About Us</span>
          <h1 class="about-hero-title">{{ pageCopy.missionTitle }}</h1>
          <p class="about-hero-sub">{{ pageCopy.heroSubtitle }}</p>
        </div>
        <div class="about-hero-visual" data-aos="fade-left">
          <div class="about-hero-img-wrap">
            <img src="https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg" alt="Our Team" />
            <div class="about-hero-badge">
              <span class="badge-dot"></span>
              Shenzhen HQ
            </div>
          </div>
          <div class="about-float-card about-float--1">
            <div class="about-float-icon about-float-icon--green">
              <i class="fa-solid fa-globe"></i>
            </div>
            <div>
              <div class="about-float-num">5+</div>
              <div class="about-float-label">Years Experience</div>
            </div>
          </div>
          <div class="about-float-card about-float--2">
            <div class="about-float-icon about-float-icon--blue">
              <i class="fa-solid fa-users"></i>
            </div>
            <div>
              <div class="about-float-num">28</div>
              <div class="about-float-label">Team Experts</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mission & Vision -->
    <section class="mv-section">
      <div class="about-inner">
        <div class="mv-grid">
          <div class="mv-card mv-card--mission" data-aos="fade-right">
            <div class="mv-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_30b96448cffb4c349febe01c7efc6a15~mv2.jpg/v1/fill/w_400,h_260,al_c,q_80,enc_avif,quality_auto/Cross-Border%20Operations%20Team_edited.jpg" alt="Mission" />
            </div>
            <div class="mv-card-body">
              <div class="mv-icon-wrap mv-icon-wrap--purple">
                <i class="fa-solid fa-bullseye"></i>
              </div>
              <h2 class="mv-title">{{ pageCopy.missionTitle }}</h2>
              <p class="mv-text">{{ pageCopy.missionText }}</p>
            </div>
          </div>
          <div class="mv-card mv-card--vision" data-aos="fade-left">
            <div class="mv-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_83df7795f2f64b4db8fd052e30d61870~mv2.png/v1/fill/w_400,h_260,al_c,q_85,enc_avif,quality_auto/3.png" alt="Vision" />
            </div>
            <div class="mv-card-body">
              <div class="mv-icon-wrap mv-icon-wrap--blue">
                <i class="fa-solid fa-eye"></i>
              </div>
              <h2 class="mv-title">{{ pageCopy.visionTitle }}</h2>
              <p class="mv-text">{{ pageCopy.visionText }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Values -->
    <section class="values-section">
      <div class="about-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Our Values</span>
          <h2 class="section-title">What We Stand For</h2>
          <p class="section-desc">The principles that guide every decision we make for our clients.</p>
        </div>
        <div class="values-grid">
          <div class="value-card" v-for="(v, vi) in coreValues" :key="vi" data-aos="fade-up" :data-aos-delay="vi * 60">
            <div class="value-icon" :class="'value-icon--' + vi">
              <i :class="['fa-solid', 'fa-' + v.icon]"></i>
            </div>
            <h3 class="value-title">{{ v.title }}</h3>
            <p class="value-desc">{{ v.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team Section with real images -->
    <section class="team-section">
      <div class="about-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Our Team</span>
          <h2 class="section-title">{{ pageCopy.teamTitle }}</h2>
          <p class="section-desc">A dedicated team of supply chain experts committed to your growth.</p>
        </div>
        <div class="team-showcase" data-aos="fade-up">
          <div class="team-img-wrap">
            <img src="https://static.wixstatic.com/media/575a2a_30b96448cffb4c349febe01c7efc6a15~mv2.jpg/v1/fill/w_600,h_350,al_c,q_80,enc_avif,quality_auto/Cross-Border%20Operations%20Team_edited.jpg" alt="Our Team" />
            <div class="team-img-overlay">
              <span>Cross-Border Operations Team</span>
            </div>
          </div>
        </div>
        <div class="team-grid">
          <div class="team-card" v-for="(member, index) in pageCopy.team" :key="index" data-aos="fade-up" :data-aos-delay="index * 80">
            <div class="team-avatar" :class="'team-avatar--' + index">
              <i :class="['fa-solid', member.icon]"></i>
            </div>
            <h3 class="team-name">{{ member.name }}</h3>
            <p class="team-role">{{ member.role }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Office / Warehouse Photos -->
    <section class="gallery-section">
      <div class="about-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Our Workspace</span>
          <h2 class="section-title">Where We Operate</h2>
          <p class="section-desc">From our Shenzhen HQ to partner warehouses worldwide.</p>
        </div>
        <div class="gallery-grid">
          <div class="gallery-item gallery-item--large" data-aos="fade-up">
            <img src="https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_600,h_400,al_c,q_80,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg" alt="Warehouse" />
            <div class="gallery-overlay">
              <span>Shenzhen Warehouse</span>
            </div>
          </div>
          <div class="gallery-item" data-aos="fade-up" data-aos-delay="100">
            <img src="https://static.wixstatic.com/media/575a2a_099a0e8719fe4183811d50c5c3bae7c1~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,enc_avif,quality_auto/1773911073629_edited.jpg" alt="Operations" />
            <div class="gallery-overlay">
              <span>Daily Operations</span>
            </div>
          </div>
          <div class="gallery-item" data-aos="fade-up" data-aos-delay="200">
            <img src="https://static.wixstatic.com/media/575a2a_9885ae085e2e4644b09a9d19b2231e0e~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,enc_avif,quality_auto/Custom%20Packaging%20Operations_edited.jpg" alt="Packaging" />
            <div class="gallery-overlay">
              <span>Custom Packaging</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="about-cta-section">
      <div class="about-cta-inner" data-aos="fade-up">
        <h2 class="about-cta-title">Ready to partner with FULFILLDROP?</h2>
        <p class="about-cta-sub">Let's build a supply chain system that scales with your ambition.</p>
        <div class="about-cta-btns">
          <button class="btn-primary btn-primary--lg" @click="scrollToContact">Get Started</button>
          <button class="btn-ghost btn-ghost--lg" @click="scrollToContact">Talk to an Expert</button>
        </div>
      </div>
    </section>

    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'
import { languages } from '../assets/languages.js'

export default {
  name: 'About',
  props: { currentLang: { type: String, default: 'en' } },
  components: { Navbar, Footer },
  data() {
    return {
      coreValues: [
        { icon: 'shield-check', title: 'Reliability', desc: 'We build systems that work consistently, not just occasionally. Every process is documented and repeatable.' },
        { icon: 'chart-line', title: 'Scalability', desc: 'Our infrastructure is designed to grow with you — from 10 orders per day to 10,000 and beyond.' },
        { icon: 'eye', title: 'Transparency', desc: 'Clear SLAs, real-time tracking, and honest communication when exceptions happen.' },
        { icon: 'handshake', title: 'Partnership', desc: 'We treat your business as if it were our own, proactively solving problems before they reach your customers.' },
      ],
      zhCopy: {
        heroSubtitle: '我们专注于跨境电商供应链执行，帮助卖家稳定增长',
        missionTitle: '我们的使命',
        missionText: '让跨境卖家把更多精力放在增长与品牌建设，而不是被供应链不确定性拖慢节奏。我们通过可执行、可追踪、可复盘的交付体系，帮助客户持续提升履约质量与效率。',
        visionTitle: '我们的愿景',
        visionText: '成为跨境品牌在中国最值得信赖的供应链执行伙伴。通过标准化流程与数字化协同，让不同阶段的卖家都能获得稳定、透明、可持续的交付能力。',
        teamTitle: '核心团队能力',
        team: [
          { name: '供应链运营', role: '采购协同与交付统筹', icon: 'fa-diagram-project' },
          { name: '仓储履约', role: '拣配标准与时效管理', icon: 'fa-boxes-stacked' },
          { name: '质量管理', role: '质检体系与异常闭环', icon: 'fa-shield-check' },
          { name: '物流与售后', role: '线路优化与售后支持', icon: 'fa-truck-fast' }
        ]
      },
      enCopy: {
        heroSubtitle: 'We focus on cross-border ecommerce supply chain execution — helping sellers achieve stable, scalable growth.',
        missionTitle: 'Our Mission',
        missionText: 'Help sellers spend more time on growth and branding, not on supply chain uncertainty. We deliver execution that is trackable, measurable, and repeatable.',
        visionTitle: 'Our Vision',
        visionText: 'Be the most trusted China-side supply chain execution partner for global ecommerce brands through standardized operations and digital coordination.',
        teamTitle: 'Core Team Capabilities',
        team: [
          { name: 'Supply Chain Ops', role: 'Sourcing coordination and execution planning', icon: 'fa-diagram-project' },
          { name: 'Warehouse Fulfillment', role: 'Pick-pack standards and SLA control', icon: 'fa-boxes-stacked' },
          { name: 'Quality Management', role: 'QC frameworks and exception handling', icon: 'fa-shield-check' },
          { name: 'Logistics & After-sales', role: 'Lane optimization and customer support', icon: 'fa-truck-fast' }
        ]
      }
    }
  },
  computed: {
    t() { return languages[this.currentLang] || languages.en },
    pageCopy() { return this.currentLang === 'zh' ? this.zhCopy : this.enCopy }
  },
  methods: {
    changeLang(lang) { this.$emit('change-lang', lang) },
    scrollToContact() {
      this.$router.push('/').then(() => {
        setTimeout(() => {
          const el = document.getElementById('contact')
          if (el) el.scrollIntoView({ behavior: 'smooth' })
        }, 300)
      })
    }
  }
}
</script>

<style scoped>
/* Base */
.about-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a; background: #fff; overflow-x: hidden;
}
.about-inner { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }
.section-header { text-align: center; margin-bottom: 4rem; }
.section-eyebrow {
  display: inline-block; font-size: 0.78rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #28b5c1; background: rgba(40,181,193,0.08);
  padding: 0.35rem 1rem; border-radius: 999px; margin-bottom: 1rem;
}
.section-title { font-size: 2.4rem; font-weight: 800; color: #0f172a; line-height: 1.15; margin-bottom: 1rem; }
.section-desc { font-size: 1.05rem; color: #64748b; max-width: 620px; margin: 0 auto; line-height: 1.75; }

/* Buttons */
.btn-primary {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: #28b5c1; color: #fff; border: none;
  padding: 0.9rem 2rem; border-radius: 10px; font-weight: 600; font-size: 1rem;
  cursor: pointer; transition: background 0.2s, transform 0.2s;
  box-shadow: 0 4px 16px rgba(40,181,193,0.28);
}
.btn-primary:hover { background: #1e9aa5; transform: translateY(-2px); }
.btn-primary--lg { padding: 1.1rem 2.5rem; font-size: 1.1rem; }
.btn-ghost {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: transparent; color: #0f172a;
  border: 1.5px solid #cbd5e1; padding: 0.9rem 2rem;
  border-radius: 10px; font-weight: 600; font-size: 1rem;
  cursor: pointer; transition: border-color 0.2s;
}
.btn-ghost:hover { border-color: #28b5c1; color: #28b5c1; }
.btn-ghost--invert { border-color: rgba(255,255,255,0.5); color: #fff; }
.btn-ghost--invert:hover { background: rgba(255,255,255,0.1); border-color: #fff; }
.btn-ghost--lg { padding: 1.1rem 2.5rem; font-size: 1.1rem; }

/* Hero */
.about-hero {
  background: linear-gradient(160deg, #f0f4ff 0%, #fafafa 60%, #fff 100%);
  padding: 6rem 0 0; overflow: hidden;
}
.about-hero-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 2rem 5rem;
  display: flex; align-items: center; gap: 4rem;
}
.about-hero-text { flex: 1; }
.about-hero-title {
  font-size: clamp(2.2rem, 3.5vw, 3rem);
  font-weight: 900; color: #0f172a; line-height: 1.1;
  margin-bottom: 1.2rem;
}
.about-hero-sub { font-size: 1.1rem; color: #475569; line-height: 1.7; max-width: 500px; }
.about-hero-visual { flex: 0 0 520px; position: relative; }
.about-hero-img-wrap {
  width: 100%; border-radius: 20px; overflow: hidden;
  box-shadow: 0 32px 64px rgba(0,0,0,0.15); position: relative;
}
.about-hero-img-wrap img { width: 100%; height: auto; display: block; aspect-ratio: 16/10; object-fit: cover; }
.about-hero-badge {
  position: absolute; bottom: 14px; left: 14px;
  background: rgba(0,0,0,0.55); backdrop-filter: blur(8px);
  color: #fff; font-size: 0.78rem; font-weight: 600;
  padding: 0.4rem 0.85rem; border-radius: 999px;
  display: flex; align-items: center; gap: 0.4rem;
}
.badge-dot { width: 7px; height: 7px; background: #22c55e; border-radius: 50%; animation: badgePulse 1.8s infinite; }
@keyframes badgePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); } 50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } }
.about-float-card {
  position: absolute; background: rgba(255,255,255,0.95); backdrop-filter: blur(12px);
  border-radius: 14px; padding: 0.9rem 1.2rem;
  box-shadow: 0 12px 32px rgba(0,0,0,0.2);
  display: flex; align-items: center; gap: 0.8rem;
  min-width: 180px; border: 1px solid rgba(255,255,255,0.3);
  animation: floatCard 3s ease-in-out infinite;
}
.about-float--1 { top: -16px; right: -20px; animation-delay: 0s; }
.about-float--2 { bottom: 30px; left: -30px; animation-delay: 1.5s; }
@keyframes floatCard { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
.about-float-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 1.1rem; }
.about-float-icon--green { background: #dcfce7; color: #16a34a; }
.about-float-icon--blue { background: #dbeafe; color: #2563eb; }
.about-float-num { font-size: 1.2rem; font-weight: 800; color: #0f172a; line-height: 1; }
.about-float-label { font-size: 0.72rem; color: #64748b; margin-top: 0.15rem; }

/* Mission & Vision */
.mv-section { padding: 7rem 0; background: #fff; }
.mv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; }
.mv-card {
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 20px;
  overflow: hidden; transition: box-shadow 0.3s, transform 0.3s;
}
.mv-card:hover { box-shadow: 0 20px 50px rgba(15,23,42,0.1); transform: translateY(-6px); }
.mv-card-img { width: 100%; height: 220px; overflow: hidden; }
.mv-card-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.mv-card:hover .mv-card-img img { transform: scale(1.05); }
.mv-card-body { padding: 2rem; }
.mv-icon-wrap {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem; margin-bottom: 1rem;
}
.mv-icon-wrap--purple { background: #d4f1f4; color: #1e9aa5; }
.mv-icon-wrap--blue { background: #dbeafe; color: #2563eb; }
.mv-title { font-size: 1.15rem; font-weight: 700; color: #0f172a; margin-bottom: 0.6rem; }
.mv-text { font-size: 0.92rem; color: #64748b; line-height: 1.7; }

/* Values */
.values-section { padding: 7rem 0; background: #f8fafc; }
.values-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem; }
.value-card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 16px;
  padding: 2rem; text-align: center;
  transition: box-shadow 0.25s, transform 0.25s;
}
.value-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.1); transform: translateY(-4px); }
.value-icon {
  width: 56px; height: 56px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; color: #fff; margin: 0 auto 1rem;
}
.value-icon--0 { background: linear-gradient(135deg,#28b5c1,#818cf8); }
.value-icon--1 { background: linear-gradient(135deg,#16a34a,#4ade80); }
.value-icon--2 { background: linear-gradient(135deg,#0891b2,#38bdf8); }
.value-icon--3 { background: linear-gradient(135deg,#d97706,#fbbf24); }
.value-title { font-size: 1rem; font-weight: 700; color: #0f172a; margin-bottom: 0.5rem; }
.value-desc { font-size: 0.85rem; color: #64748b; line-height: 1.65; }

/* Team Section */
.team-section { padding: 7rem 0; background: #fff; }
.team-showcase {
  width: 100%; border-radius: 20px; overflow: hidden;
  margin-bottom: 3rem; position: relative;
}
.team-showcase img { width: 100%; height: auto; display: block; }
.team-img-overlay {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: 1rem 1.5rem;
  background: linear-gradient(transparent, rgba(15,23,42,0.7));
  color: #fff; font-size: 0.9rem; font-weight: 600;
}
.team-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem; }
.team-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 2rem; text-align: center; transition: box-shadow 0.25s, transform 0.25s; }
.team-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.1); transform: translateY(-4px); }
.team-avatar {
  width: 56px; height: 56px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; color: #fff; margin: 0 auto 1rem;
}
.team-avatar--0 { background: linear-gradient(135deg,#28b5c1,#818cf8); }
.team-avatar--1 { background: linear-gradient(135deg,#0891b2,#38bdf8); }
.team-avatar--2 { background: linear-gradient(135deg,#16a34a,#4ade80); }
.team-avatar--3 { background: linear-gradient(135deg,#d97706,#fbbf24); }
.team-name { font-size: 1rem; font-weight: 700; color: #0f172a; margin-bottom: 0.3rem; }
.team-role { font-size: 0.82rem; color: #64748b; }

/* Gallery Section */
.gallery-section { padding: 7rem 0; background: #f8fafc; }
.gallery-grid { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 1.5rem; }
.gallery-item {
  border-radius: 16px; overflow: hidden; position: relative;
  box-shadow: 0 8px 24px rgba(15,23,42,0.08); cursor: pointer;
}
.gallery-item--large { grid-row: span 2; }
.gallery-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.gallery-item:hover img { transform: scale(1.05); }
.gallery-overlay {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: 0.8rem 1.2rem;
  background: linear-gradient(transparent, rgba(15,23,42,0.7));
  color: #fff; font-size: 0.82rem; font-weight: 600;
}

/* CTA */
.about-cta-section {
  padding: 7rem 2rem;
  background: linear-gradient(135deg, #28b5c1 0%, #1e9aa5 60%, #06b6d4 100%);
  text-align: center; position: relative; overflow: hidden;
}
.about-cta-inner { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }
.about-cta-title { font-size: 2.4rem; font-weight: 800; color: #fff; line-height: 1.2; margin-bottom: 1rem; }
.about-cta-sub { font-size: 1.05rem; color: rgba(255,255,255,0.8); margin-bottom: 2.5rem; }
.about-cta-btns { display: flex; justify-content: center; flex-wrap: wrap; gap: 1rem; }
.about-cta-section .btn-primary { background: #fff; color: #28b5c1; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
.about-cta-section .btn-primary:hover { background: #f0f4ff; }
.about-cta-section .btn-ghost { border-color: rgba(255,255,255,0.5); color: #fff; }
.about-cta-section .btn-ghost:hover { background: rgba(255,255,255,0.1); border-color: #fff; }

/* Responsive */
@media (max-width: 1024px) {
  .mv-grid { grid-template-columns: 1fr; }
  .values-grid { grid-template-columns: repeat(2, 1fr); }
  .team-grid { grid-template-columns: repeat(2, 1fr); }
  .gallery-grid { grid-template-columns: 1fr 1fr; }
  .about-hero-inner { gap: 2rem; }
  .about-hero-visual { flex: 0 0 400px; }
}
@media (max-width: 768px) {
  .about-hero-inner { flex-direction: column; padding-bottom: 3rem; }
  .about-hero-visual { flex: none; width: 100%; }
  .about-float-card { display: none; }
  .values-grid { grid-template-columns: 1fr; }
  .team-grid { grid-template-columns: 1fr; }
  .gallery-grid { grid-template-columns: 1fr; }
  .section-title { font-size: 2rem; }
  .about-hero-title { font-size: 2.2rem; }
  .about-cta-title { font-size: 1.9rem; }
}
</style>
